import { useReducer, useCallback } from 'react'

import {
  ADDRESS_TYPE_CODE,
  STEPS,
  ADDRESS_STATES,
  ADDRESS_SOURCES,
} from '../../common/constants'
import { useUserContext } from '../../auth/UserProvider'
import { useShippingSendMutation } from '../../services/shippingSend'
import ShippingSendUI from '../../ui/views/shippingSend'
import { generateReportPDF } from './generateReportPDF'
import useTranslations from '../../i18n/useTranslations'

import formatSubmitData from './formatSubmitData'
import { useUploadFiles } from '../../services/user'
import { generateRandomKey } from '../../utils'
import { MD5 } from 'crypto-js'

const { STATUS } = ShippingSendUI
const STEPS_ORDER = [
  STEPS.FROM,
  STEPS.TO,
  STEPS.SHIPMENT,
  STEPS.CUSTOMS,
  STEPS.SERVICES,
  STEPS.SUMMARY,
]

const NEXT_STEP_FOR = {
  [STEPS.FROM]: STEPS.TO,
  [STEPS.TO]: STEPS.SHIPMENT,
  [STEPS.SHIPMENT]: STEPS.SERVICES,
  [STEPS.SERVICES]: STEPS.SUMMARY,
  [STEPS.SUMMARY]: null,
}

const PREVIOUS_STEP_FOR = {
  [STEPS.FROM]: null,
  [STEPS.TO]: STEPS.FROM,
  [STEPS.SHIPMENT]: STEPS.TO,
  [STEPS.SERVICES]: STEPS.SHIPMENT,
  [STEPS.SUMMARY]: STEPS.SERVICES,
}

const NEXT_STEP_FOR_CUSTOMS = {
  [STEPS.FROM]: STEPS.TO,
  [STEPS.TO]: STEPS.SHIPMENT,
  [STEPS.SHIPMENT]: STEPS.CUSTOMS,
  [STEPS.CUSTOMS]: STEPS.SERVICES,
  [STEPS.SERVICES]: STEPS.SUMMARY,
  [STEPS.SUMMARY]: null,
}

const PREVIOUS_STEP_FOR_CUSTOMS = {
  [STEPS.FROM]: null,
  [STEPS.TO]: STEPS.FROM,
  [STEPS.SHIPMENT]: STEPS.TO,
  [STEPS.CUSTOMS]: STEPS.SHIPMENT,
  [STEPS.SERVICES]: STEPS.CUSTOMS,
  [STEPS.SUMMARY]: STEPS.SERVICES,
}

const getActiveStep = values => {
  if (!values) {
    return STEPS_ORDER[0]
  }

  for (const step of STEPS_ORDER.slice().reverse()) {
    if (!!values[step]) {
      return values?.shipment?.hasCustom?.custom
        ? NEXT_STEP_FOR_CUSTOMS[step]
        : NEXT_STEP_FOR[step]
    }
  }

  return STEPS_ORDER[0]
}

const getFirstErroredStep = state => {
  if (!state?.fieldErrors) {
    return null
  }

  for (const step of STEPS_ORDER) {
    if (step !== state.currentStep && !!state?.fieldErrors[step]) {
      return step
    }
  }

  return null
}

const transformAddressTypeToFormStep = addressType => {
  return addressType === ADDRESS_TYPE_CODE.ORIGIN ? 'from' : 'to'
}

const getStepState = (state, currentStep) =>
  state?.dirty[currentStep]
    ? STATUS.ENABLED
    : !!state?.fieldErrors[currentStep]
    ? STATUS.ERROR
    : !!state?.values[currentStep]
    ? STATUS.SUCCESS
    : state?.values?.shipment?.hasCustom?.custom
    ? !PREVIOUS_STEP_FOR_CUSTOMS[currentStep]
      ? STATUS.ENABLED
      : PREVIOUS_STEP_FOR_CUSTOMS[currentStep] === STEPS.SHIPMENT
      ? !!state?.values[PREVIOUS_STEP_FOR_CUSTOMS[currentStep]] &&
        Object.keys(state?.values[PREVIOUS_STEP_FOR_CUSTOMS[currentStep]])
          .length > 1
        ? STATUS.ENABLED
        : STATUS.DISABLED
      : !!state?.values[PREVIOUS_STEP_FOR_CUSTOMS[currentStep]]
      ? STATUS.ENABLED
      : STATUS.DISABLED
    : !PREVIOUS_STEP_FOR[currentStep]
    ? STATUS.ENABLED
    : !!state?.values[PREVIOUS_STEP_FOR[currentStep]] &&
      Object.keys(state?.values[PREVIOUS_STEP_FOR[currentStep]]).length > 1
    ? STATUS.ENABLED
    : STATUS.DISABLED

const getStepStates = state => {
  const stepStates = {
    [STEPS.FROM]: getStepState(state, STEPS.FROM),
    [STEPS.TO]: getStepState(state, STEPS.TO),
    [STEPS.SHIPMENT]: getStepState(state, STEPS.SHIPMENT),
    [STEPS.CUSTOMS]: state?.values?.shipment?.hasCustom?.custom
      ? getStepState(state, STEPS.CUSTOMS)
      : undefined,
    [STEPS.SERVICES]: getStepState(state, STEPS.SERVICES),
    [STEPS.SUMMARY]:
      state.formErrors !== null
        ? STATUS.ERROR
        : state?.values?.shipment?.hasCustom?.custom
        ? !!state?.values[PREVIOUS_STEP_FOR_CUSTOMS[STEPS.SUMMARY]]
          ? STATUS.ENABLED
          : STATUS.DISABLED
        : !!state?.values[PREVIOUS_STEP_FOR[STEPS.SUMMARY]]
        ? STATUS.ENABLED
        : STATUS.DISABLED,
  }

  Object.keys(stepStates).forEach(
    key => stepStates[key] === undefined && delete stepStates[key],
  )
  return stepStates
}

const INITIAL_STATE = {
  isSubmitting: false,
  originOrDestinAddressChanged: false,
  originAddressChanged: false,
  destinAddressChanged: false,
  currentStep: STEPS.FROM,
  confirmationAction: null,
  dirty: {
    [STEPS.FROM]: false,
    [STEPS.TO]: false,
    [STEPS.SHIPMENT]: false,
    [STEPS.CUSTOMS]: false,
    [STEPS.SERVICES]: false,
  },
  values: {
    [STEPS.FROM]: null,
    [STEPS.TO]: null,
    [STEPS.SHIPMENT]: null,
    [STEPS.CUSTOMS]: null,
    [STEPS.SERVICES]: null,
  },
  fieldErrors: {
    [STEPS.FROM]: null,
    [STEPS.TO]: null,
    [STEPS.SHIPMENT]: null,
    [STEPS.CUSTOMS]: null,
    [STEPS.SERVICES]: null,
  },
  formErrors: null,
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'save-values': {
      const confirmationAction = state.confirmationAction

      // NOTE: en el paso 3 se encuentra la selección de tarifas. Para obtenerlas,
      // se llama a un endpont pasándole el código postal de origen, país de origen,
      // código postal de destino y país de destino.
      // Durante la creación de un envío, es posible que el usuario
      // cambie los datos postales de origen o destino. Debido a esto, es
      // necesario comprobar si alguno de estos dos datos ha cambiado para
      // redirigirle al paso 3 y que seleccione nuevamente una tarifa, dado
      // que pueden haber cambiado en base a las direcciones nuevas.

      const originAddressChanged =
        state.currentStep === STEPS.FROM && state.values[STEPS.FROM] !== null
          ? state.values[STEPS.FROM].address.postalCode !==
              action.values.address.postalCode ||
            (state.values[STEPS.FROM].addressSource ===
              ADDRESS_SOURCES.pickUpPoint &&
              state.values[STEPS.TO]?.addressState ===
                ADDRESS_STATES.AT_PICKUP_POINT)
          : false

      const destinAddressChanged =
        state.currentStep === STEPS.TO && state.values[STEPS.TO] !== null
          ? state.values[STEPS.FROM].address.postalCode !==
            action.values.address.postalCode
          : false

      const originOrDestinAddressChanged =
        originAddressChanged || destinAddressChanged

      const values = originOrDestinAddressChanged
        ? {
            ...state.values,
            [state.currentStep]: action.values,
            [STEPS.SHIPMENT]: null,
            [STEPS.SERVICES]: null,
          }
        : {
            ...state.values,
            [state.currentStep]: action.values,
          }

      // Añadir esta lógica para resetear el destino si el origen es PUDO
      if (
        state.currentStep === STEPS.FROM &&
        action.values.addressSource === ADDRESS_SOURCES.pickUpPoint &&
        state.values[STEPS.TO]?.addressState === ADDRESS_STATES.AT_PICKUP_POINT
      ) {
        values[STEPS.TO] = null
      }

      const savedState = {
        ...state,
        originOrDestinAddressChanged,
        originAddressChanged,
        destinAddressChanged,
        confirmationAction: null,
        dirty: {
          [STEPS.FROM]: false,
          [STEPS.TO]: false,
          [STEPS.SHIPMENT]: false,
          [STEPS.CUSTOMS]: false,
          [STEPS.SERVICES]: false,
        },
        values,
        fieldErrors: {
          ...state.fieldErrors,
          [state.currentStep]: null,
        },
        formErrors: null,
      }

      // If saving from the confirmation modal
      if (confirmationAction) {
        return reducer(savedState, confirmationAction)
      }
      // Saving from the form
      else {
        const activeStep = getActiveStep(state.values)
        const erroredStep = getFirstErroredStep(state)

        const nextStep = originAddressChanged
          ? // Si el origen ha cambiado y el destino está null, significa que es
            // la primera vez que el usuario está rellenando el formulario y que,
            // además, ha rellenado los datos de origen, ha dado a continuar y luego
            // ha vuelto al origen para corregir la dirección. En este caso,
            // el siguiente paso solo puede ser el destino.
            // En caso contrario, significaría que el usuario llegó hasta el paso 3
            // o posterior y cambió el origen. Aquí se le vuelve a mandar al paso 3.
            state.values[STEPS.TO] === null
            ? STEPS.TO
            : STEPS.SHIPMENT
          : // Si ha cambiado el destino, su siguiente paso únicamente puede ser el envío.
          destinAddressChanged
          ? STEPS.SHIPMENT
          : !state.values[state.currentStep]
          ? state?.values?.shipment?.hasCustom?.custom
            ? NEXT_STEP_FOR_CUSTOMS[state.currentStep]
            : NEXT_STEP_FOR[state.currentStep]
          : erroredStep
          ? erroredStep
          : activeStep

        return {
          ...savedState,
          currentStep: nextStep,
        }
      }
    }

    case 'go-to': {
      // Do nothing if we're already on the step we're moving to
      if (action.step === state.currentStep) {
        return state
      }

      // Disable step tabs while submitting
      if (state.isSubmitting) {
        return state
      }

      if (state.dirty[state.currentStep]) {
        return {
          ...state,
          confirmationAction: action,
        }
      }

      return {
        ...state,
        confirmationAction: null,
        dirty: {
          [STEPS.FROM]: false,
          [STEPS.TO]: false,
          [STEPS.SHIPMENT]: false,
          [STEPS.CUSTOMS]: false,
          [STEPS.SERVICES]: false,
        },
        currentStep: action.step,
      }
    }

    case 'save-values-custom': {
      return {
        ...state,
        values: {
          ...state.values,
          shipment: {
            ...state.values.shipment,
            hasCustom: action.values,
          },
        },
        isSubmitting: state.isSubmitting,
        fieldErrors: {
          [STEPS.FROM]: null,
          [STEPS.TO]: null,
          [STEPS.SHIPMENT]: null,
          [STEPS.CUSTOMS]: null,
          [STEPS.SERVICES]: null,
        },
        formErrors: null,
      }
    }

    case 'set-dirty': {
      return {
        ...state,
        dirty: {
          ...state.dirty,
          [state.currentStep]: action.dirty,
        },
      }
    }

    case 'cancel-confirmation': {
      return {
        ...state,
        confirmationAction: null,
      }
    }

    case 'accept-confirmation': {
      const confirmationAction = state.confirmationAction

      if (confirmationAction === null) {
        return state
      }

      return reducer(
        {
          ...state,
          confirmationAction: null,
          dirty: {
            [STEPS.FROM]: false,
            [STEPS.TO]: false,
            [STEPS.SHIPMENT]: false,
            [STEPS.CUSTOMS]: false,
            [STEPS.SERVICES]: false,
          },
        },
        confirmationAction,
      )
    }

    case 'submit-attempt': {
      return {
        ...state,
        isSubmitting: true,
        fieldErrors: {
          [STEPS.FROM]: null,
          [STEPS.TO]: null,
          [STEPS.SHIPMENT]: null,
          [STEPS.CUSTOMS]: null,
          [STEPS.SERVICES]: null,
        },
        formErrors: null,
      }
    }

    case 'server-errors': {
      return {
        ...state,
        isSubmitting: false,
        fieldErrors: {
          [STEPS.FROM]: null,
          [STEPS.TO]: null,
          [STEPS.SHIPMENT]: null,
          [STEPS.CUSTOMS]: null,
          [STEPS.SERVICES]: null,
          ...action.fieldErrors,
        },
        formErrors: action.formErrors ?? null,
      }
    }

    case 'update-pickup-error': {
      return {
        ...state,
        values: {
          ...state.values,
          from: {
            ...state.values.from,
          },
        },
        fieldErrors: {
          ...state.fieldErrors,
          from: {
            ...state.fieldErrors.from,
            pickUpTimeSlot: action.error,
          },
        },
      }
    }

    case 'save-new-address': {
      const step = transformAddressTypeToFormStep(action.origin)

      return {
        ...state,
        values: {
          ...state.values,
          [step]: {
            ...state.values[action.origin],
            ...action.addressData,
            addressState: 'COMMON',
          },
        },
      }
    }

    default: {
      return state
    }
  }
}

const useShippingSend = () => {
  const { auditData } = useUserContext()
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const { clientId, centres, webUserName } = useUserContext()
  const centreCodeId = centres.find(
    ({ id }) => id === state?.values?.from?.imputationCenter?.id,
  )?.hideId

  const { mutateAsync: uploadFileAsync, isError: isErrorFile } = useUploadFiles(
    clientId,
    centreCodeId,
  )

  const processFile = file => {
    return new Promise((resolve, reject) => {
      const readFileAsync = file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            const base64String = reader.result.split(',')[1]
            const md5Hash = MD5(base64String).toString()
            resolve({
              id: generateRandomKey(),
              file: file,
              type: 'COMMERCIAL_INVOICE',
              fileBodyPOST: {
                file_group: 'export-info',
                file_type: 'COMMERCIAL_INVOICE',
                file_name: file.name,
                file_extension: file.name.split('.').pop(),
                file_size: file.size,
                compression_type: 'None',
                md5: md5Hash,
                data: base64String,
              },
            })
          }
          reader.onerror = error =>
            reject(new Error('Failed to read file: ' + error))
          reader.readAsDataURL(file)
        })
      }

      const handleFilesAsync = async () => {
        try {
          const fileToAdd = await readFileAsync(file)
          resolve(fileToAdd)
        } catch (error) {
          console.error('Error in handleFilesAsync:', error)
          reject(error)
        }
      }

      return handleFilesAsync()
    })
  }

  const t = useTranslations()

  const submitMutation = useShippingSendMutation({
    useErrorBoundary: false,
    onError: (error, _variables, _context) => {
      // Store server errors
      dispatch({
        type: 'server-errors',
        fieldErrors: error.data?.fieldErrors,
        formErrors: error.data?.errors,
      })
    },
    onSuccess: (data, _variables, _context) => {
      return Promise.resolve(data.data)
    },
  })

  const handleSubmitSuccess = useCallback(values => {
    dispatch({ type: 'save-values', values })
  }, [])

  const handleSubmitCustomSuccess = useCallback(values => {
    dispatch({ type: 'save-values-custom', values })
  }, [])

  const handleClickStep = useCallback(step => {
    dispatch({ type: 'go-to', step })
  }, [])

  const handleSetDirty = useCallback(dirty => {
    dispatch({ type: 'set-dirty', dirty })
  }, [])

  const handleCancelConfirmation = useCallback(() => {
    dispatch({ type: 'cancel-confirmation' })
  }, [])

  const handleAcceptConfirmation = useCallback(() => {
    dispatch({ type: 'accept-confirmation' })
  }, [])

  const handleSubmit = useCallback(async () => {
    dispatch({ type: 'submit-attempt' })
    let values = { ...state.values }

    if (state.values.customs) {
      try {
        const file = await generateReportPDF(state.values, t, webUserName)
        const fileDataForUpload = await processFile(file)
        const uploadResult = await uploadFileAsync(
          fileDataForUpload.fileBodyPOST,
        )
        if (!values.customs.additionalDocumentation) {
          values.customs.additionalDocumentation = []
        }
        fileDataForUpload.resource_id = uploadResult.resource_id
        fileDataForUpload.resource_provider = uploadResult.resource_provider
        values.customs.generateReportPDF = fileDataForUpload
      } catch (error) {
        console.error(
          'Error en la generación del PDF o en la carga del archivo:',
          error,
        )
      }
    }
    const formattedData = formatSubmitData(values, auditData)
    const newShipment = await submitMutation.mutateAsync(formattedData)

    return Promise.resolve(newShipment.data.shipping_code)
  }, [state.values, uploadFileAsync, submitMutation, t, auditData])

  const handleUpdatePickupError = useCallback(error => {
    dispatch({ type: 'update-pickup-error', error })
  }, [])

  const activeStep = getActiveStep(state?.values)

  const stepStates = getStepStates(state)

  return {
    state,
    activeStep,
    stepStates,
    handleSubmitSuccess,
    handleClickStep,
    handleSetDirty,
    handleCancelConfirmation,
    handleAcceptConfirmation,
    handleSubmit,
    handleUpdatePickupError,
    handleSubmitCustomSuccess,
    isSubmitting: submitMutation.isLoading,
  }
}

export default useShippingSend
